import { PageRef } from 'providers/page-ref';
import { IEditIngredientPage } from 'pages/edit-ingredient/edit-ingredient-interface';
import { buildLogPortionScreenParams } from 'pages/edit-ingredient/log-mode-open';
import { FoodsPageResult } from './foods-interface';

/**
 * The add-food errand: what should happen to a food once the user has it (#4968, #5377).
 *
 * A user adding a food is on an errand that outlives any one screen. They start it from a meal
 * or from a day, and whichever page ends up PRODUCING the food has to finish it: the food list
 * when they tap a food, the food editor when a label scan creates one. This module is the one
 * place that knows how to finish it, so those pages cannot end the errand differently.
 *
 * The errand travels as navigation parameters, and every page between the start and the page
 * that produces the food forwards them. It has two forms:
 *
 * - **somebody is waiting** — `returnCallback` names the page method to answer, and
 *   `local.returnContext` is handed back to it;
 * - **log it to a day** — `gotoLogFoodAfterSelect_dayDate` opens the portion screen on that day.
 *
 * The rule and the reasons are in `design-docs/navigation.md`, "The add-food errand".
 */

/**
 * What a waiting page expects to receive.
 *
 * The food editor has always answered with a bare food id, and the portion screen and the food
 * list read it that way. The meal screen and the day list read a `FoodsPageResult` instead,
 * because until #5377 only the food list answered them. Both are reached through the same
 * `returnCallback` parameter, so the caller has to say which one it wants: the callback's name
 * cannot tell them apart.
 */
export enum ErrandAnswerShape {
  FoodId = 'foodId',
  FoodsPageResult = 'foodsPageResult',
}

/** The navigation parameters that carry an errand. Every page on the route forwards them. */
export type AddFoodErrandParams = {
  returnCallback?: string;
  errandAnswerShape?: ErrandAnswerShape;
  gotoLogFoodAfterSelect_dayDate?: string;
  keepOpenAfterSelect?: boolean;
  local?: { returnContext?: unknown };
};

/** An errand as read off a page's parameters, with its answer shape decided. */
export interface AddFoodErrand {
  returnCallback?: string;
  returnContext?: unknown;
  answerShape: ErrandAnswerShape;
  dayDate?: string;
  keepOpenAfterSelect: boolean;
}

/** Which page is finishing the errand. It decides whether that page stays, closes, or is replaced. */
export enum ErrandAnswerer {
  FoodList = 'foodList',
  FoodEditor = 'foodEditor',
}

/** The parts of `NavController` this module uses, so it can be tested without Angular. */
export interface ErrandNavigation {
  popAsync(result?: unknown): Promise<unknown>;
  pushAsync(pageRef: PageRef<any, any>, params?: any): Promise<unknown>;
  replaceTopAsync(pageRef: PageRef<any, any>, params?: any): Promise<unknown>;
  deliverResultWithoutPoppingAsync(returnCallback: string, returnContext: unknown, result: unknown): Promise<unknown>;
  queuePostBrowserNavCallback(callback: () => void): void;
  isBrowserNavigationInProgress(): boolean;
}

/** The same shape as `CanLeaveResult` in nav-controller.ts, repeated so this file needs no Angular. */
export type ErrandCanLeaveResult<T> = boolean | { result: T };

/**
 * Read the errand off a page's parameters.
 *
 * `defaultShape` is the page's own answer when the caller did not name one: the food list has
 * always answered with a `FoodsPageResult`, the food editor with a bare id.
 *
 * A caller expecting a `FoodsPageResult` always passes a context, and every one of those owners
 * checks it. A missing one means the stored `local` params were lost — iOS evicts sessionStorage
 * when the app is backgrounded (Sentry 3SQ) — and the owner will then quietly do nothing with
 * the food. So it is reported, and the answer is still delivered. The portion screen's own
 * "view details" route opens the editor with a callback and no context on purpose, and answers
 * with a bare id, so that shape is not checked.
 */
export function readAddFoodErrand(
  params: AddFoodErrandParams,
  defaultShape: ErrandAnswerShape,
  reportProblem: (name: string, context: Record<string, unknown>) => void,
): AddFoodErrand {
  const errand: AddFoodErrand = {
    returnCallback: params.returnCallback || undefined,
    returnContext: params.local?.returnContext,
    answerShape: params.errandAnswerShape || defaultShape,
    dayDate: params.gotoLogFoodAfterSelect_dayDate || undefined,
    keepOpenAfterSelect: !!params.keepOpenAfterSelect,
  };
  if (errand.returnCallback && errand.answerShape === ErrandAnswerShape.FoodsPageResult && errand.returnContext === undefined) {
    reportProblem('AddFoodErrandContextMissing', { returnCallback: errand.returnCallback });
  }
  return errand;
}

/**
 * Was this page opened as part of adding a food somewhere, rather than for browsing?
 *
 * A food list with neither form was opened to look at foods (Settings → Foods), and finishing
 * a food there is the whole errand.
 */
export function hasAddFoodIntent(errand: AddFoodErrand): boolean {
  return !!errand.returnCallback || !!errand.dayDate;
}

/** The value a waiting page receives for this food. */
function answerFor(errand: AddFoodErrand, foodId: string): FoodsPageResult | string {
  return errand.answerShape === ErrandAnswerShape.FoodsPageResult ? { action: 'select', foodId } : foodId;
}

/** The portion screen, in Log mode, for a day errand. */
function portionScreenParams(errand: AddFoodErrand, foodId: string) {
  return buildLogPortionScreenParams({ dayDate: errand.dayDate!, pickedId: foodId });
}

/**
 * Finish the errand with a food the user now has.
 *
 * - **Somebody is waiting:** the answering page closes with the answer, which the nav controller
 *   hands to the waiting page. The food list is the exception when its caller asked it to stay
 *   open (`keepOpenAfterSelect`, #4967): it answers without closing, so Log comes back to it.
 * - **Log it to a day:** the food list opens the portion screen ON TOP of itself, so Log and Back
 *   return to it. The food editor REPLACES itself with the portion screen instead (#5377): the
 *   user never saw a list on this route, and should not land on a food editor they have finished
 *   with. `replaceTop` runs no leave guard, which is right, because the food has just been saved.
 * - **No errand:** close. The food editor closes with the food's id, as it always has, for the
 *   caller that opened it with no errand.
 *
 * `beforePop` runs only when the page is about to be closed by a pop. The food editor arms its
 * skip-the-leave-guard flag there, and only there: armed before a `replaceTop` that the next
 * page refused, the flag would let the user's next Back skip the guard once.
 */
export async function answerAddFoodErrandAsync(
  nav: ErrandNavigation,
  errand: AddFoodErrand,
  foodId: string,
  answerer: ErrandAnswerer,
  beforePop?: () => void,
): Promise<void> {
  if (errand.returnCallback) {
    const answer = answerFor(errand, foodId);
    if (answerer === ErrandAnswerer.FoodList && errand.keepOpenAfterSelect) {
      await nav.deliverResultWithoutPoppingAsync(errand.returnCallback, errand.returnContext, answer);
      return;
    }
    beforePop?.();
    await nav.popAsync(answer);
    return;
  }

  if (errand.dayDate) {
    if (answerer === ErrandAnswerer.FoodEditor) {
      await nav.replaceTopAsync(IEditIngredientPage, portionScreenParams(errand, foodId));
    } else {
      await nav.pushAsync(IEditIngredientPage, portionScreenParams(errand, foodId));
    }
    return;
  }

  beforePop?.();
  await nav.popAsync(answerer === ErrandAnswerer.FoodEditor ? foodId : undefined);
}

/**
 * What the food editor hands over when it is closed by a leave (Back, or Save in the
 * unsaved-changes dialog), rather than by its own Accept button.
 *
 * Nothing unless a save actually completed: a food being created carries an id from the moment
 * the page builds it, long before it exists (#4968). Otherwise the same answer Accept gives.
 */
export function errandLeaveAnswer(errand: AddFoodErrand, savedFoodId: string | undefined): FoodsPageResult | string | undefined {
  if (!savedFoodId) return undefined;
  return answerFor(errand, savedFoodId);
}

/**
 * Finish a DAY errand when the food editor is left by Back after saving from the dialog (#5377).
 *
 * A waiting page gets its answer from the leave itself — see `errandLeaveAnswer`. A day errand
 * has nobody waiting, so the portion screen has to be opened. It cannot be opened from inside
 * the leave: by then the browser has already gone back one entry, and replacing the page there
 * rewrites the wrong history entry, after which Back on the portion screen does nothing. So the
 * leave is REFUSED, which puts the history entry back for the editor still on screen, and the
 * replace is queued to run once the browser's back handling has finished. The end state is the
 * one Accept reaches: the portion screen where the editor was.
 *
 * Only while a browser back is being handled. Every ordinary Back reaches the editor that way;
 * a leave that did not (a sign-out resetting the stack) must not queue anything, or the replace
 * would fire on some unrelated later Back. If a native back ever arrives without the browser
 * layer, the food is still saved and simply not logged.
 */
export function finishDayErrandOnLeave<T>(
  nav: ErrandNavigation,
  errand: AddFoodErrand,
  verdict: ErrandCanLeaveResult<T>,
  savedFoodId: string | undefined,
  reportError: (err: unknown) => void,
): ErrandCanLeaveResult<T> {
  if (verdict === false) return verdict;
  if (errand.returnCallback || !errand.dayDate || !savedFoodId) return verdict;
  if (!nav.isBrowserNavigationInProgress()) return verdict;

  nav.queuePostBrowserNavCallback(() => {
    nav.replaceTopAsync(IEditIngredientPage, portionScreenParams(errand, savedFoodId)).catch(reportError);
  });
  return false;
}
